import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import TrackToggles from './TrackToggles';
import {
  DRUM_KEY,
  DRUM_COLOR,
  DRUM_LABEL,
  GM_FAMILIES,
  PALETTE,
  colorForKey,
  drawEmptyState,
  familyColor,
  fullLabelForKey,
  groupKeyForTrack,
  parseMeasures,
  pitchToName,
  programToFamily,
  roundedRect,
  useElementWidth,
  useJsonResource,
  usePlayhead,
} from './pianoRollShared';
import './PianoRoll.css';

// Expected JSON shape:
//   {
//     "tracks": [
//       { "name"?: string, "program": int, "is_drum"?: bool,
//         "notes": [{"pitch": int, "time": float, "duration": float, "velocity": int}] },
//       ...
//     ],
//     "click"?: { "notes": [{"pitch": 34 | 32, "time": float}, ...] }
//   }
// The click track is optional; without it the roll simply has no bar lines.
const MultitrackPianoRoll = ({ jsonUrl, audioRef }) => {
  const canvasRef = useRef(null);
  const playheadRef = useRef(null);
  const wrapperRef = useRef(null);
  const layoutRef = useRef(null);

  const { data, error } = useJsonResource(jsonUrl);
  const width = useElementWidth(wrapperRef);
  const [visibleGroups, setVisibleGroups] = useState({});

  const presentGroups = useMemo(() => {
    if (!data || !Array.isArray(data.tracks)) return [];

    const seenFamilies = new Set();
    let hasDrums = false;
    for (const track of data.tracks) {
      if (!track || !Array.isArray(track.notes) || track.notes.length === 0) continue;
      if (track.is_drum) {
        hasDrums = true;
        continue;
      }
      seenFamilies.add(programToFamily(track.program));
    }

    const groups = [...seenFamilies]
      .sort((a, b) => a - b)
      .map((famIdx) => ({
        key: `fam-${famIdx}`,
        label: GM_FAMILIES[famIdx].short,
        title: GM_FAMILIES[famIdx].full,
        color: familyColor(famIdx),
      }));

    if (hasDrums) {
      groups.push({ key: DRUM_KEY, label: DRUM_LABEL, title: DRUM_LABEL, color: DRUM_COLOR });
    }
    return groups;
  }, [data]);

  useEffect(() => {
    const initial = {};
    presentGroups.forEach((g) => { initial[g.key] = true; });
    setVisibleGroups(initial);
  }, [presentGroups]);

  const drawPianoRoll = useCallback(() => {
    if (!data || !canvasRef.current || !Array.isArray(data.tracks)) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const allNotes = [];
    for (const track of data.tracks) {
      if (!track || !Array.isArray(track.notes)) continue;
      const key = groupKeyForTrack(track);
      if (!visibleGroups[key]) continue;
      const color = colorForKey(key);
      for (const note of track.notes) {
        allNotes.push({ ...note, color });
      }
    }

    const canvasWidth = canvas.offsetWidth;

    if (allNotes.length === 0) {
      drawEmptyState(canvas, ctx, dpr, 'No instruments selected');
      layoutRef.current = null;
      return;
    }

    const downbeats = parseMeasures(data.click?.notes || []);
    const numMeasures = downbeats.length;

    // Audio is silence-trimmed at both ends, so shift the MIDI timeline left by
    // the first onset to keep audio and roll aligned at t = 0.
    const timeShift = Math.max(0, Math.min(...allNotes.map((n) => n.time)));

    const minPitch = Math.min(...allNotes.map((n) => n.pitch)) - 1;
    const maxPitch = Math.max(...allNotes.map((n) => n.pitch)) + 1;
    const maxTime = Math.max(
      ...allNotes.map((n) => (n.time - timeShift) + Math.max(n.duration || 0.08, 0.08))
    );
    const pitchRange = maxPitch - minPitch + 1;

    const noteHeight = Math.max(2, Math.min(8, 240 / pitchRange));
    const height = Math.max(140, pitchRange * noteHeight + 48);
    const leftPad = 36;
    const topPad = 24;
    const drawWidth = canvasWidth - leftPad - 4;

    // maxTime is the post-shift duration, so the playhead's
    // audio.currentTime / maxTime mapping stays correct.
    layoutRef.current = { leftPad, drawWidth, maxTime, topPad, height };

    canvas.width = canvasWidth * dpr;
    canvas.height = height * dpr;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    const timeToX = (t) => leftPad + ((t - timeShift) / maxTime) * drawWidth;

    ctx.fillStyle = PALETTE.background;
    ctx.fillRect(0, 0, canvasWidth, height);

    if (numMeasures > 0) {
      for (let i = 0; i < numMeasures; i += 1) {
        const x0 = timeToX(downbeats[i]);
        const x1 = i + 1 < numMeasures ? timeToX(downbeats[i + 1]) : timeToX(maxTime + timeShift);
        ctx.fillStyle = i % 2 === 0 ? PALETTE.measureEven : PALETTE.measureOdd;
        ctx.fillRect(x0, topPad, x1 - x0, height - topPad);
      }

      for (let i = 0; i < numMeasures; i += 1) {
        const x = timeToX(downbeats[i]);
        ctx.strokeStyle = PALETTE.measureLine;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, topPad);
        ctx.lineTo(x, height);
        ctx.stroke();

        const nextX = i + 1 < numMeasures ? timeToX(downbeats[i + 1]) : timeToX(maxTime + timeShift);
        ctx.fillStyle = PALETTE.measureLabel;
        ctx.font = '10px Inter, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${i + 1}`, (x + nextX) / 2, topPad - 8);
      }

      for (const beat of (data.click?.notes || []).filter((n) => n.pitch === 32)) {
        const x = timeToX(beat.time);
        ctx.strokeStyle = PALETTE.beatLine;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(x, topPad);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
    }

    for (let p = minPitch; p <= maxPitch; p += 1) {
      if (p % 12 !== 0) continue;
      const y = topPad + (maxPitch - p) * noteHeight;
      ctx.strokeStyle = PALETTE.pitchLine;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(leftPad, y);
      ctx.lineTo(canvasWidth, y);
      ctx.stroke();
      ctx.fillStyle = PALETTE.pitchLabel;
      ctx.font = '9px Inter, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(pitchToName(p), leftPad - 4, y + 3);
    }

    for (const note of allNotes) {
      const x = timeToX(note.time);
      const y = topPad + (maxPitch - note.pitch) * noteHeight;
      const w = Math.max(2, (Math.max(note.duration || 0.06, 0.06) / maxTime) * drawWidth);
      const h = Math.max(2, noteHeight - 1);

      ctx.fillStyle = note.color;
      ctx.globalAlpha = 0.85;
      roundedRect(ctx, x, y, w, h, 2);
      ctx.globalAlpha = 1;
    }
  }, [data, visibleGroups]);

  useEffect(() => {
    drawPianoRoll();
  }, [drawPianoRoll, width]);

  usePlayhead(audioRef, layoutRef, playheadRef);

  const toggleGroup = useCallback((key) => {
    setVisibleGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setAllGroups = useCallback((value) => {
    setVisibleGroups((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => { next[key] = value; });
      return next;
    });
  }, []);

  if (error) {
    return <div className="piano-roll-error">Visualization unavailable</div>;
  }

  if (!data) {
    return <div className="piano-roll-loading" />;
  }

  return (
    <div className="piano-roll-wrapper">
      <TrackToggles
        groups={presentGroups.map((g) => ({ ...g, title: fullLabelForKey(g.key) }))}
        visible={visibleGroups}
        onToggle={toggleGroup}
        onSetAll={setAllGroups}
      />
      <div className="piano-roll-canvas-wrapper" ref={wrapperRef}>
        <canvas ref={canvasRef} className="piano-roll-canvas" />
        <div ref={playheadRef} className="piano-roll-playhead" />
      </div>
    </div>
  );
};

export default MultitrackPianoRoll;
